import {
  useNotify,
  useDevice,
  GoogleOneTapSignIn,
  VerifyPasswordModal,
  Reason,
} from '@treatwell/ui';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { loadAccountDetails } from 'js/redux-modules/account/details/actions';
import { connect } from 'react-redux';
import { isProductionEnvironment } from '../helpers/environment';
import {
  useOnError,
  handleCredentialResponse,
  onSuccess,
  onModalDisplay,
  onModalDismiss,
  fetchLogin,
  getTrackingEventErrorMessage,
  trackGoogleLoginError,
} from '../helpers/googleOneTap/googleOneTap';
import { StateData } from '../model/rainbow/StateData';
import { AccountDetailsState } from '../model/state/account/details';
import { ReduxState } from '../model/state';
import { getAccountDetails } from '../redux-modules/account/selectors';

const DisplayGoogleOneTap = ({
  pageData,
  accountDetails,
  loadAccountDetails,
}: {
  pageData: StateData;
  accountDetails: AccountDetailsState;
  loadAccountDetails: () => unknown;
}) => {
  useEffect(() => {
    loadAccountDetails();
  }, [loadAccountDetails]);

  const { user } = accountDetails;
  const { notifyError } = useNotify();
  const { t } = useTranslation();
  const genericError = t('error.generic');
  const { isMobile } = useDevice();
  const shouldShowGoogleOneTap =
    /homepage|browse/.test(pageData.pageType) && user && !user.isAuthenticated;
  const triggerContext = shouldShowGoogleOneTap
    ? (pageData.pageType as 'homepage' | 'browse')
    : undefined;
  const countryCode = pageData.channel.country.countryCode;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [input, setInputValue] = useState('');
  const [googleToken, setGoogleToken] = useState('');
  const [errorText, setErrorText] = useState<string>();

  const handleTriggerVerifyModal = useCallback(() => setIsModalOpen(true), []);
  const onHandleError = useOnError({
    channel: pageData.channel,
    genericError,
    isMobile,
    notifyError,
    eventTriggerContext: triggerContext,
    onTriggerVerifyModal: handleTriggerVerifyModal,
  });

  const onVerifyClick = () => {
    setErrorText(undefined);
    fetchLogin({ oneTapToken: googleToken, password: input })
      .then(() => {
        window.location.reload();
      })
      .catch((error) => {
        setErrorText(genericError);
        const message = getTrackingEventErrorMessage(error);

        trackGoogleLoginError({
          countryCode,
          isWebMobile: isMobile,
          triggerContext,
          error: `Password verification: ${message}`,
        });
      });
  };

  const handleOnGoogleToken = useCallback((token: string) => {
    setGoogleToken(token);
  }, []);

  const handleOnModalDisplay = useCallback(
    (args?: { auto: true }) => {
      onModalDisplay({
        countryCode,
        isWebMobile: isMobile,
        triggerContext,
        auto: args !== undefined ? args?.auto : undefined,
      });
    },
    [countryCode, isMobile, triggerContext]
  );

  const handleOnModalDismiss = useCallback(
    (reason: Reason) =>
      onModalDismiss({
        reason,
        countryCode,
        isWebMobile: isMobile,
        triggerContext,
      }),
    [countryCode, isMobile, triggerContext]
  );

  return (
    <>
      {shouldShowGoogleOneTap && (
        <>
          <GoogleOneTapSignIn
            environment={isProductionEnvironment ? 'prod' : 'dev'}
            handleCredentialResponse={handleCredentialResponse}
            onError={onHandleError}
            onSuccess={onSuccess(countryCode, isMobile, triggerContext)}
            onGoogleToken={handleOnGoogleToken}
            onModalDisplay={handleOnModalDisplay}
            onModalDismiss={handleOnModalDismiss}
          />
          <VerifyPasswordModal
            labels={{
              description: t('verifyPassword.description'),
              placeholder: t('verifyPassword.placeholder'),
              cta: {
                verify: t('verifyPassword.cta.submit'),
                cancel: t('verifyPassword.cta.cancel'),
              },
            }}
            onToggle={(state) => setIsModalOpen(state)}
            onVerifyClick={onVerifyClick}
            passwordValue={input}
            onPasswordChange={(event) => {
              setInputValue(event.currentTarget.value);

              if (errorText) {
                setErrorText(undefined);
              }
            }}
            isOpen={isModalOpen}
            errorText={errorText}
          />
        </>
      )}
    </>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  accountDetails: getAccountDetails(state),
});

const mapDispatchToProps = {
  loadAccountDetails,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DisplayGoogleOneTap);
